import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronLeft,
  Volume2,
  VolumeX,
  Megaphone,
  MegaphoneOff,
  Lock,
  Unlock,
  Shield,
  Gem,
} from "lucide-react";
import confetti from "canvas-confetti";
import { Card } from "@/types/linguadeck";
import { useGamification } from "@/contexts/GamificationContext";
import { playAudio } from "@/lib/audioUtils";
import { useSoundStore } from "@/stores/useSoundStore";
import { playPop, playSuccess, playError } from "@/lib/studySfx";
import { useVictoryMusic } from "@/hooks/useVictoryMusic";
import { GameResultsScreen } from "@/components/study/GameResultsScreen";

const CRYSTALS_BASE = 5;
const MAX_HACK_ATTEMPTS = 3;
const DECOY_MIN = 3;
const DECOY_MAX = 5;

const LATIN_DECOYS = "AEIOUBCDFGHJKLMNPQRSTVWXYZ";
const CYRILLIC_DECOYS = "АЕИОУБВГДЖЗЙКЛМНПРСТФХЦЧШЩЪЫЬЭЮЯ";

function shuffleArray<T>(arr: T[]): T[] {
  const copy = [...arr];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

function getDecoyAlphabet(word: string): string {
  if (/[\u0400-\u04FF]/.test(word)) return CYRILLIC_DECOYS;
  return LATIN_DECOYS;
}

function pickDecoys(targetChars: string[], count: number): string[] {
  const word = targetChars.join("");
  const alphabet = getDecoyAlphabet(word);
  const existing = new Set(word.toUpperCase().split(""));
  const candidates = alphabet.split("").filter((c) => !existing.has(c));
  const decoys: string[] = [];
  for (let i = 0; i < count && candidates.length > 0; i++) {
    const idx = Math.floor(Math.random() * candidates.length);
    decoys.push(candidates[idx]!);
    candidates.splice(idx, 1);
  }
  return decoys;
}

export interface PoolLetter {
  id: string;
  char: string;
}

interface CipherRebusModeProps {
  cards: Card[];
  lang?: string;
  onBack?: () => void;
  onProgressComplete?: (pct: number) => void;
}

function FloatingCrystals({
  amount,
  onComplete,
}: {
  amount: number;
  onComplete: () => void;
}) {
  return (
    <motion.div
      className="pointer-events-none fixed inset-0 flex items-center justify-center z-50"
      initial={false}
    >
      <motion.span
        className="inline-flex items-center gap-1.5 text-3xl font-bold text-[hsl(var(--accent2))] drop-shadow-lg whitespace-nowrap"
        initial={{ opacity: 1, y: 0, scale: 1 }}
        animate={{ opacity: 0, y: -80, scale: 1.2 }}
        transition={{ duration: 1.2, ease: "easeOut" }}
        onAnimationComplete={onComplete}
      >
        +{amount}
        <Gem className="w-8 h-8" aria-hidden />
      </motion.span>
    </motion.div>
  );
}

export default function CipherRebusMode({
  cards,
  lang: deckLang,
  onBack,
  onProgressComplete,
}: CipherRebusModeProps) {
  const { t: tFn } = useTranslation();
  const t = typeof tFn === "function" ? tFn : ((key: string, opts?: object) => key);
  const { addCrystals, finishStudySession } = useGamification();
  const {
    isSoundEnabled,
    isVoiceEnabled,
    gameVolume,
    voiceVolume,
    setGameVolume,
    setVoiceVolume,
    toggleSound,
    toggleVoice,
  } = useSoundStore();

  const filteredCards = useMemo(
    () =>
      cards.filter((c) => (c.front || "").trim() && (c.back || "").trim()),
    [cards]
  );

  const [cardIndex, setCardIndex] = useState(0);
  const [filledSlots, setFilledSlots] = useState<(PoolLetter | null)[]>([]);
  const [poolLetters, setPoolLetters] = useState<PoolLetter[]>([]);
  const [hackAttempts, setHackAttempts] = useState(0);
  const [slotShake, setSlotShake] = useState(false);
  const [slotFlash, setSlotFlash] = useState(false);
  const [success, setSuccess] = useState(false);
  const [floatingCrystals, setFloatingCrystals] = useState<number | null>(null);
  const [isGameFinished, setIsGameFinished] = useState(false);
  const [sessionCrystals, setSessionCrystals] = useState(0);

  useVictoryMusic({
    isGameFinished,
    isSoundEnabled,
    gameVolume,
  });


  const currentCard = filteredCards[cardIndex] ?? null;
  const total = filteredCards.length;

  const targetChars = useMemo(() => {
    const term = (currentCard?.front ?? "").trim();
    return term ? term.split("") : [];
  }, [currentCard]);

  const buildInitialPool = useCallback(() => {
    if (targetChars.length === 0) return [];
    const decoyCount =
      DECOY_MIN + Math.floor(Math.random() * (DECOY_MAX - DECOY_MIN + 1));
    const decoys = pickDecoys(targetChars, decoyCount);
    const cardId = currentCard?.id ?? cardIndex;
    const correctLetters: PoolLetter[] = targetChars.map((char, i) => ({
      id: `cipher-${cardId}-slot-${i}`,
      char,
    }));
    const decoyLetters: PoolLetter[] = decoys.map((_, i) => ({
      id: `cipher-${cardId}-decoy-${i}`,
      char: decoys[i]!,
    }));
    return shuffleArray([...correctLetters, ...decoyLetters]);
  }, [currentCard?.id, cardIndex, targetChars]);

  useEffect(() => {
    if (!currentCard || targetChars.length === 0) return;
    setFilledSlots(targetChars.map(() => null));
    setPoolLetters(buildInitialPool());
    setHackAttempts(0);
    setSlotShake(false);
    setSlotFlash(false);
    setSuccess(false);
  }, [currentCard, cardIndex, targetChars.length]);

  const nextSlotIndex = useMemo(
    () => filledSlots.findIndex((s) => s === null),
    [filledSlots]
  );

  const triggerWrongFeedback = useCallback(() => {
    setSlotShake(true);
    setSlotFlash(true);
    if (isSoundEnabled) playError(gameVolume * 0.8);
    setTimeout(() => setSlotShake(false), 400);
    setTimeout(() => setSlotFlash(false), 500);
  }, [isSoundEnabled, gameVolume]);

  const resetWord = useCallback(() => {
    setFilledSlots(targetChars.map(() => null));
    setPoolLetters(buildInitialPool());
    setHackAttempts(0);
    setSlotShake(false);
    setSlotFlash(false);
  }, [targetChars, buildInitialPool]);

  const handlePlayAgain = useCallback(() => {
    setIsGameFinished(false);
    setSessionCrystals(0);
    setCardIndex(0);
  }, []);

  const handleBackToDeck = useCallback(() => {
    onBack?.();
  }, [onBack]);

  const handlePoolLetterClick = useCallback(
    (letter: PoolLetter) => {
      if (success || !currentCard || nextSlotIndex < 0) return;
      const expectedChar = targetChars[nextSlotIndex];
      if (!expectedChar) return;

      if (letter.char === expectedChar) {
        if (isSoundEnabled) playPop(gameVolume * 0.6);
        setFilledSlots((prev) => {
          const next = [...prev];
          next[nextSlotIndex] = letter;
          return next;
        });
        setPoolLetters((prev) => prev.filter((l) => l.id !== letter.id));

        const newFilled = [...filledSlots];
        newFilled[nextSlotIndex] = letter;
        const allFilled = newFilled.every((s) => s !== null);
        if (allFilled) {
          setSuccess(true);
          const term = (currentCard.front || "").trim();
          if (isVoiceEnabled && term) {
            const frontLang = currentCard.frontLang ?? deckLang;
            setTimeout(
              () => void playAudio({
                card: currentCard,
                side: "front",
                deckLang: frontLang,
                volume: voiceVolume,
              }),
              200
            );
          }
          if (isSoundEnabled) playSuccess(gameVolume * 0.7);
          confetti({
            colors: ["#8b5cf6", "#3b82f6", "#10b981", "#f59e0b"],
            origin: { y: 0.7 },
            particleCount: 100,
            spread: 50,
          });
          const currentLevel = cardIndex + 1;
          const rewardAmount = CRYSTALS_BASE * Math.max(1, currentLevel);
          const result = addCrystals(
            rewardAmount,
            t("cipherRebus.rewardReason"),
            { isLearningAction: true, silent: true }
          );
          const totalAdd =
            typeof result === "object" ? result.totalAdd : rewardAmount;
          setFloatingCrystals(totalAdd);
          setSessionCrystals((prev) => prev + totalAdd);
          setTimeout(() => {
            setFloatingCrystals(null);
            const nextIndex = cardIndex + 1;
            if (total > 0 && onProgressComplete) {
              const pct = Math.min(100, Math.round((nextIndex / total) * 100));
              onProgressComplete(pct);
            }
            if (cardIndex >= total - 1) {
              finishStudySession(total, false);
              setIsGameFinished(true);
            } else {
              setCardIndex((i) => i + 1);
            }
          }, 1400);
        }
      } else {
        triggerWrongFeedback();
        const nextAttempts = hackAttempts + 1;
        setHackAttempts(nextAttempts);
        if (nextAttempts >= MAX_HACK_ATTEMPTS) {
          setTimeout(resetWord, 500);
        }
      }
    },
    [
      success,
      currentCard,
      nextSlotIndex,
      targetChars,
      filledSlots,
      hackAttempts,
      isSoundEnabled,
      gameVolume,
      isVoiceEnabled,
      voiceVolume,
      deckLang,
      cardIndex,
      total,
      addCrystals,
      finishStudySession,
      t,
      triggerWrongFeedback,
      resetWord,
      onProgressComplete,
    ]
  );

  const headerRow = (
    <header className="flex justify-between items-center w-full max-w-2xl mx-auto mb-6 gap-4">
      <div className="min-w-0 flex-1">
        {onBack ? (
          <button
            type="button"
            onClick={onBack}
            className="flex items-center gap-2 px-4 py-2 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card-bg))] hover:bg-[hsl(var(--bg3))] text-[hsl(var(--foreground))] font-medium transition-colors"
            aria-label={t("studyMenu.backToDeck")}
          >
            <ChevronLeft className="w-4 h-4 shrink-0" aria-hidden />
            {t("studyMenu.backToDeck")}
          </button>
        ) : (
          <div />
        )}
      </div>
      <div className="grid grid-cols-[64px_auto] gap-x-2 gap-y-1 items-center shrink-0">
        <input
          type="range"
          min="0"
          max="1"
          step="0.05"
          value={gameVolume}
          onChange={(e) => setGameVolume(parseFloat(e.target.value))}
          className="w-16 h-1.5 accent-[hsl(var(--primary))] cursor-pointer justify-self-end"
          aria-label={t("common.gameVolume")}
        />
        <button
          type="button"
          onClick={toggleSound}
          className={`p-2 rounded-lg hover:bg-[hsl(var(--bg3))] transition-colors w-9 h-9 flex items-center justify-center ${isSoundEnabled ? "text-[hsl(var(--primary))]" : "text-[hsl(var(--muted-foreground))] opacity-50"}`}
          aria-label={
            isSoundEnabled
              ? t("common.muteMusic")
              : t("common.unmuteMusic")
          }
        >
          {isSoundEnabled ? (
            <Volume2 className="w-5 h-5" />
          ) : (
            <VolumeX className="w-5 h-5" />
          )}
        </button>
        <input
          type="range"
          min="0"
          max="1"
          step="0.05"
          value={voiceVolume}
          onChange={(e) => setVoiceVolume(parseFloat(e.target.value))}
          className="w-16 h-1.5 accent-[hsl(var(--primary))] cursor-pointer justify-self-end"
          aria-label={t("common.voiceVolume")}
        />
        <button
          type="button"
          onClick={toggleVoice}
          className={`p-2 rounded-lg hover:bg-[hsl(var(--bg3))] transition-colors w-9 h-9 flex items-center justify-center ${isVoiceEnabled ? "text-[hsl(var(--primary))]" : "text-[hsl(var(--muted-foreground))] opacity-50"}`}
          aria-label={
            isVoiceEnabled
              ? t("common.voiceModeOn")
              : t("common.voiceModeOff")
          }
        >
          {isVoiceEnabled ? (
            <Megaphone className="w-5 h-5" />
          ) : (
            <MegaphoneOff className="w-5 h-5" />
          )}
        </button>
      </div>
    </header>
  );

  if (filteredCards.length === 0) {
    return (
      <div className="flex flex-col w-full max-w-lg mx-auto px-4">
        {headerRow}
        <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card-bg))] p-8 text-center">
          <p className="text-lg font-medium text-[hsl(var(--foreground))]">
            {t("cipherRebus.needCards")}
          </p>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2">
            {t("cipherRebus.addMoreCards")}
          </p>
        </div>
      </div>
    );
  }

  if (isGameFinished) {
    return (
      <GameResultsScreen
        sessionCrystals={sessionCrystals}
        onPlayAgain={handlePlayAgain}
        onBackToDeck={handleBackToDeck}
        headerRow={headerRow}
      />
    );
  }

  return (
    <div className="flex flex-col w-full max-w-2xl mx-auto px-4 pb-10 relative">
      <AnimatePresence>
        {floatingCrystals != null && (
          <FloatingCrystals
            amount={floatingCrystals}
            onComplete={() => setFloatingCrystals(null)}
          />
        )}
      </AnimatePresence>

      {headerRow}

      <div className="mb-4 flex items-center justify-between gap-4 flex-wrap">
        <span className="text-sm text-[hsl(var(--muted-foreground))]">
          {t("cipherRebus.progress", {
            current: cardIndex + 1,
            total,
            defaultValue: `${cardIndex + 1} / ${total}`,
          })}
        </span>
        <div className="flex items-center gap-2">
          <Shield
            className="w-4 h-4 text-[hsl(var(--muted-foreground))]"
            aria-hidden
          />
          <span className="text-sm font-medium text-[hsl(var(--muted-foreground))]">
            {t("attempts_count", "{{current}}/{{total}} attempts", {
              current: hackAttempts,
              total: MAX_HACK_ATTEMPTS,
            })}
          </span>
        </div>
      </div>

      <motion.div
        className={`rounded-2xl border-2 overflow-hidden transition-colors ${
          success
            ? "border-[hsl(var(--accent2))] ring-2 ring-[hsl(var(--accent2)/0.4)]"
            : "border-[hsl(var(--border))]"
        }`}
        style={{
          background:
            "linear-gradient(145deg, hsl(var(--card-bg) / 0.98), hsl(var(--muted) / 0.15))",
          boxShadow:
            "0 8px 32px rgba(0,0,0,0.08), 0 0 0 1px hsl(var(--border) / 0.5)",
        }}
      >
        <div className="p-6 md:p-8">
          <p className="text-sm font-medium text-[hsl(var(--muted-foreground))] mb-1 flex items-center gap-2">
            <Lock className="w-4 h-4" />
            {t("word_label", "Word")}
          </p>
          <p className="text-xl font-semibold text-[hsl(var(--foreground))] mb-6 break-words">
            {currentCard?.back}
          </p>

          <p className="text-sm font-medium text-[hsl(var(--muted-foreground))] mb-3 flex items-center gap-2">
            {success ? (
              <Unlock className="w-4 h-4 text-[hsl(var(--accent2))]" />
            ) : (
              <Lock className="w-4 h-4" />
            )}
            {t("decode_the_word", "Decode the word")}
          </p>

          <div
            className={`min-h-[56px] flex flex-wrap gap-2 p-4 rounded-xl mb-6 transition-colors ${
              slotFlash
                ? "bg-red-500/10 border-2 border-red-500/60 dark:border-red-500/50"
                : "bg-[hsl(var(--muted)/0.25)] border-2 border-[hsl(var(--border))]"
            } ${slotShake ? "animate-shake" : ""}`}
            style={{
              boxShadow: "inset 0 1px 2px rgba(0,0,0,0.04)",
            }}
          >
            <AnimatePresence mode="popLayout">
              {filledSlots.map((slot, index) => (
                <motion.div
                  key={slot ? slot.id : `empty-${index}`}
                  layout
                  layoutId={slot?.id}
                  initial={false}
                  className="inline-flex items-center justify-center min-w-[44px] h-12 rounded-xl font-bold text-lg select-none"
                  style={{
                    background: slot
                      ? "linear-gradient(145deg, hsl(var(--primary)), hsl(var(--primary) / 0.85))"
                      : "hsl(var(--muted) / 0.4)",
                    color: slot
                      ? "hsl(var(--primary-foreground))"
                      : "hsl(var(--muted-foreground) / 0.5)",
                    boxShadow: slot
                      ? "0 2px 8px rgba(0,0,0,0.12), 0 0 0 1px rgba(255,255,255,0.1) inset"
                      : "inset 0 1px 2px rgba(0,0,0,0.06)",
                    border: slot
                      ? "1px solid hsl(var(--primary) / 0.5)"
                      : "1px dashed hsl(var(--border))",
                  }}
                  transition={{
                    type: "spring",
                    stiffness: 400,
                    damping: 30,
                  }}
                >
                  {slot ? slot.char : ""}
                </motion.div>
              ))}
            </AnimatePresence>
          </div>

          {success ? (
            <p className="text-center font-semibold text-[hsl(var(--accent2))] flex items-center justify-center gap-2">
              <Unlock className="w-5 h-5" />
              {t("cipherRebus.correct")}
            </p>
          ) : (
            <>
              <p className="text-sm text-[hsl(var(--muted-foreground))] mb-3">
                {t("pick_correct_letters_hint", "Pick the correct letters in order")}
              </p>
              <div className="flex flex-wrap gap-2">
                {poolLetters.map((letter) => (
                  <motion.button
                    key={letter.id}
                    layout
                    layoutId={letter.id}
                    type="button"
                    onClick={() => handlePoolLetterClick(letter)}
                    whileHover={{ scale: 1.06 }}
                    whileTap={{ scale: 0.96 }}
                    className="inline-flex items-center justify-center min-w-[44px] h-12 rounded-xl font-bold text-lg border-2 border-[hsl(var(--border))] bg-[hsl(var(--card-bg))] hover:border-[hsl(var(--primary)/0.6)] hover:bg-[hsl(var(--bg3))] text-[hsl(var(--foreground))] transition-colors shadow-sm hover:shadow-md"
                    style={{
                      boxShadow:
                        "0 2px 6px rgba(0,0,0,0.06), 0 0 0 1px hsl(var(--border) / 0.3)",
                    }}
                  >
                    {letter.char}
                  </motion.button>
                ))}
              </div>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
}
